import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Copy, Location, SampleUser } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import Modal from '../../../componants/modal/Modal'
import { showToast } from '../../../componants/toast/toastService'
import GroupChat from './groupChat'
import './groupDetail.css'
import './addExpense.css'
import './createGroup.css'

// The old Settle Up tab (Simplified Debts/Settle Channels/History) had no
// backend support at all and was dropped per the user's ask. The header
// "Settle Up" button now just toggles group.settlement_status via
// PATCH groupDetail — the one settle-related thing the real API supports
// at the group level (per-expense settling lives in expenseDetail.jsx).
// Chat is real now (see groupChat.jsx) — added between Overview/Expenses.
const TABS = ['Overview', 'Chat', 'Expenses']

const THEME_BACKGROUNDS = [
    'linear-gradient(135deg, #FDBA74 0%, #FB923C 100%)',
    'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)',
    'linear-gradient(135deg, #86EFAC 0%, #4ADE80 100%)',
    'linear-gradient(135deg, #FCD34D 0%, #FBBF24 100%)',
]

const EXPENSE_FILTERS = ['All', 'You Paid', 'Others Paid']

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#1D2A4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const CameraIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" stroke="#9CA3AF" strokeWidth="1.4" strokeLinejoin="round" />
        <circle cx="12" cy="13.5" r="3.2" stroke="#9CA3AF" strokeWidth="1.4" />
    </svg>
)

const EditIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M11.3 2.3a1 1 0 0 1 1.4 0l1 1a1 1 0 0 1 0 1.4L5.4 13H3v-2.4l8.3-8.3Z" stroke="#1D2A4D" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
)

const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
            d="M2 4h12M5.5 4V2.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V4m-7 0 .7 8.4a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L12.5 4"
            stroke="#E7000B"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const BellIcon = () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
        <path
            d="M8 1.5c-2 0-3.5 1.6-3.5 3.6v2.1c0 .4-.15.8-.42 1.1L3 9.6c-.5.55-.1 1.4.63 1.4h8.74c.73 0 1.13-.85.63-1.4l-1.08-1.3a1.6 1.6 0 0 1-.42-1.1V5.1C11.5 3.1 10 1.5 8 1.5Z"
            stroke="#026D4D"
            strokeWidth="1.3"
            strokeLinejoin="round"
        />
        <path d="M6.3 13.2a1.9 1.9 0 0 0 3.4 0" stroke="#026D4D" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
)

// Same guess-across-field-names fallback as createGroup.jsx — the real
// /splitwise/bookings/ response shape isn't confirmed yet.
const bookingLabel = (booking) => {
    const title = booking.title || booking.name || booking.service_name || booking.booking_number || `Booking #${booking.id}`
    const meta = booking.date_label || booking.date || booking.service || ''
    return meta ? `${title} · ${meta}` : title
}

const LocationPin = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 14.6667S13 10.5 13 6.6667a5 5 0 1 0-10 0C3 10.5 8 14.6667 8 14.6667Z" stroke="#026D4D" strokeWidth="1.4" strokeLinejoin="round" />
        <circle cx="8" cy="6.6667" r="1.6667" stroke="#026D4D" strokeWidth="1.4" />
    </svg>
)

const formatDate = (value) => {
    if (!value) return ''
    const date = new Date(String(value).replace(' ', 'T'))
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const capitalize = (value) => (value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : '')

const formatMoney = (value) => `₹${(Number(value) || 0).toLocaleString('en-IN')}`

const GroupDetail = ({ groupId: groupIdProp, onBack, onAddExpense, onOpenExpense }) => {
    const groupId = Number(groupIdProp) || null
    const [activeTab, setActiveTab] = useState(TABS[0])
    const [expenseFilter, setExpenseFilter] = useState(EXPENSE_FILTERS[0])
    const [group, setGroup] = useState(null)
    const [loading, setLoading] = useState(true)
    const [expenses, setExpenses] = useState([])
    const [expensesLoading, setExpensesLoading] = useState(true)

    const [addMemberOpen, setAddMemberOpen] = useState(false)
    const [newMemberRows, setNewMemberRows] = useState([{ name: '', email: '' }])
    const [addingMembers, setAddingMembers] = useState(false)
    const [manageMember, setManageMember] = useState(null)
    const [manageEmail, setManageEmail] = useState('')
    const [memberActionLoading, setMemberActionLoading] = useState(false)
    const [sendingReminderId, setSendingReminderId] = useState(null)
    const [editGroupOpen, setEditGroupOpen] = useState(false)
    const [editName, setEditName] = useState('')
    const [editTag, setEditTag] = useState('')
    const [editStatus, setEditStatus] = useState('open')
    const [editIconFile, setEditIconFile] = useState(null)
    const [editIconPreview, setEditIconPreview] = useState('')
    const editIconInputRef = useRef(null)
    const [savingGroup, setSavingGroup] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [deletingGroup, setDeletingGroup] = useState(false)
    const [shareBookingOpen, setShareBookingOpen] = useState(false)
    const [shareBookings, setShareBookings] = useState([])
    const [shareBookingsLoading, setShareBookingsLoading] = useState(false)
    const [shareBookingIds, setShareBookingIds] = useState([])
    const [sharingBooking, setSharingBooking] = useState(false)
    const [unlinkingBookingId, setUnlinkingBookingId] = useState(null)
    const [settleUpOpen, setSettleUpOpen] = useState(false)
    const [togglingSettlement, setTogglingSettlement] = useState(false)

    const fetchGroup = () => {
        if (!groupId) return Promise.resolve()
        return service
            .get(endpoints.splitwise.groupDetail.replace('{group_id}', groupId), {}, { silent: true })
            .then(({ data }) => setGroup(data.data))
            .catch(() => setGroup(null))
    }

    useEffect(() => {
        if (!groupId) { setLoading(false); return }
        setLoading(true)
        fetchGroup().finally(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId])

    useEffect(() => {
        if (!groupId) { setExpensesLoading(false); return }
        setExpensesLoading(true)
        service
            .get(endpoints.splitwise.groupExpenses.replace('{group_id}', groupId), {}, { silent: true })
            .then(({ data }) => setExpenses(Array.isArray(data.data) ? data.data : []))
            .catch(() => setExpenses([]))
            .finally(() => setExpensesLoading(false))
    }, [groupId])

    // The group-detail response's members[] carries a `user` field (the
    // backend user id) even though it's undocumented — matching it against
    // the logged-in user resolves "which SplitMember row is me" reliably,
    // unlike the group *list* endpoint which has no is_owner/admin marker.
    const currentUserId = useSelector((state) => state.auth.user?.id)
    const youMemberId = useMemo(() => {
        if (currentUserId == null) return null
        const me = (group?.members || []).find((m) => m.user === currentUserId)
        return me ? me.id : null
    }, [group, currentUserId])

    const themeBackground = THEME_BACKGROUNDS[(group?.id || 0) % THEME_BACKGROUNDS.length]

    const mappedExpenses = useMemo(() => expenses.map((expense) => {
        const yourShareEntry = youMemberId != null ? (expense.shares || []).find((s) => s.member === youMemberId) : null
        return {
            id: expense.id,
            title: expense.title,
            paidByName: expense.paid_by_name || `Member #${expense.paid_by}`,
            paidById: expense.paid_by,
            splitType: expense.split_type,
            status: expense.payment_status === 'full_paid' ? 'settled' : 'pending',
            amount: Number(expense.amount) || 0,
            yourShare: yourShareEntry ? Number(yourShareEntry.share_amount) || 0 : null,
        }
    }), [expenses, youMemberId])

    const visibleExpenses = mappedExpenses.filter((expense) => {
        if (expenseFilter === 'You Paid') return youMemberId != null && expense.paidById === youMemberId
        if (expenseFilter === 'Others Paid') return !(youMemberId != null && expense.paidById === youMemberId)
        return true
    })

    const totalSpend = Number(group?.total_spend) || 0
    const yourShare = Number(group?.your_share) || 0
    const youOwe = Number(group?.you_owe) || 0
    const youSettled = Math.max(0, yourShare - youOwe)

    // group.owner is the backend user id (not a SplitMember id) — compare
    // directly against the logged-in user to gate owner-only actions
    // (add/remove member, edit a pending member's email).
    const isOwner = group?.owner != null && currentUserId != null && group.owner === currentUserId

    const openAddMember = () => {
        setNewMemberRows([{ name: '', email: '' }])
        setAddMemberOpen(true)
    }

    const updateNewMemberRow = (index, field, value) => {
        setNewMemberRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
    }

    const addNewMemberRow = () => setNewMemberRows((prev) => [...prev, { name: '', email: '' }])
    const removeNewMemberRow = (index) => setNewMemberRows((prev) => prev.filter((_, i) => i !== index))

    const handleAddMembers = () => {
        const validRows = newMemberRows
            .map((row) => ({ name: row.name.trim(), email: row.email.trim() }))
            .filter((row) => row.name && row.email)
        if (!validRows.length || addingMembers) return
        setAddingMembers(true)
        service
            .post(endpoints.splitwise.groupMembers.replace('{group_id}', groupId), { members: validRows })
            .then(() => {
                setAddMemberOpen(false)
                fetchGroup()
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setAddingMembers(false))
    }

    const openManageMember = (member) => {
        setManageMember(member)
        setManageEmail(member.email || '')
    }

    const closeManageMember = () => setManageMember(null)

    const handleSaveEmail = () => {
        if (!manageMember || memberActionLoading) return
        const email = manageEmail.trim()
        if (!email) return
        setMemberActionLoading(true)
        service
            .patch(endpoints.splitwise.memberEmail.replace('{member_id}', manageMember.id), { email })
            .then(() => {
                closeManageMember()
                fetchGroup()
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setMemberActionLoading(false))
    }

    const handleRemoveMember = () => {
        if (!manageMember || memberActionLoading) return
        setMemberActionLoading(true)
        service
            .delete(endpoints.splitwise.memberDelete.replace('{member_id}', manageMember.id))
            .then(() => {
                closeManageMember()
                fetchGroup()
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setMemberActionLoading(false))
    }

    // Not restricted to the owner per the API doc — unlike the update-group,
    // add-members, and update-member-email endpoints (all explicitly marked
    // "(owner)" in the doc's own endpoint index), payment-reminder carries
    // no such annotation, so any group member can nudge someone with a
    // pending due. Sends {member_id} for one person, or an empty body to
    // remind everyone who currently owes.
    const handleSendReminder = (memberId) => {
        if (sendingReminderId) return
        setSendingReminderId(memberId || 'all')
        service
            .post(endpoints.splitwise.paymentReminder.replace('{group_id}', groupId), memberId ? { member_id: memberId } : {})
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSendingReminderId(null))
    }

    const openEditGroup = () => {
        setEditName(group?.name || '')
        setEditTag(group?.tag || '')
        setEditStatus(group?.status || 'open')
        setEditIconFile(null)
        setEditIconPreview(group?.icon || '')
        setEditGroupOpen(true)
    }

    const handleEditIconChange = (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setEditIconFile(file)
        const reader = new FileReader()
        reader.onload = () => setEditIconPreview(reader.result)
        reader.readAsDataURL(file)
    }

    const handleSaveGroup = () => {
        if (!editName.trim() || savingGroup) return
        setSavingGroup(true)
        let payload
        if (editIconFile) {
            payload = new FormData()
            payload.append('name', editName.trim())
            payload.append('tag', editTag.trim())
            payload.append('status', editStatus)
            payload.append('icon', editIconFile)
        } else {
            payload = {
                name: editName.trim(),
                tag: editTag.trim(),
                status: editStatus,
            }
        }
        service
            .patch(endpoints.splitwise.groupDetail.replace('{group_id}', groupId), payload)
            .then(() => {
                setEditGroupOpen(false)
                fetchGroup()
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSavingGroup(false))
    }

    const openShareBooking = () => {
        setShareBookingIds([])
        setShareBookingOpen(true)
        setShareBookingsLoading(true)
        service
            .get(endpoints.splitwise.bookings, {}, { silent: true })
            .then(({ data }) => setShareBookings(Array.isArray(data.data) ? data.data : []))
            .catch(() => setShareBookings([]))
            .finally(() => setShareBookingsLoading(false))
    }

    const addShareBooking = (id) => {
        if (!id) return
        const numId = Number(id)
        if (shareBookingIds.includes(numId)) return
        setShareBookingIds((prev) => [...prev, numId])
    }

    const removeShareBooking = (id) => {
        setShareBookingIds((prev) => prev.filter((bid) => bid !== id))
    }

    const handleShareBookings = () => {
        if (!shareBookingIds.length || sharingBooking) return
        setSharingBooking(true)
        const nextBookingIds = [...new Set([...(group?.booking_ids || []), ...shareBookingIds])]
        service
            .patch(endpoints.splitwise.groupDetail.replace('{group_id}', groupId), { booking_ids: nextBookingIds })
            .then(() => {
                setShareBookingOpen(false)
                fetchGroup()
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSharingBooking(false))
    }

    const removeLinkedBooking = (id) => {
        if (unlinkingBookingId) return
        setUnlinkingBookingId(id)
        const nextBookingIds = (group?.booking_ids || []).filter((bid) => bid !== id)
        service
            .patch(endpoints.splitwise.groupDetail.replace('{group_id}', groupId), { booking_ids: nextBookingIds })
            .then(() => fetchGroup())
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setUnlinkingBookingId(null))
    }

    const handleToggleSettlement = () => {
        if (togglingSettlement) return
        const nextStatus = group.settlement_status === 'settled' ? 'unsettled' : 'settled'
        setTogglingSettlement(true)
        service
            .patch(endpoints.splitwise.groupDetail.replace('{group_id}', groupId), { settlement_status: nextStatus })
            .then(() => {
                setSettleUpOpen(false)
                fetchGroup()
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setTogglingSettlement(false))
    }

    const handleCopyInvite = () => {
        if (!group?.invitation_link) return
        navigator.clipboard?.writeText(group.invitation_link)
        showToast('Invite link copied', 'success')
    }

    const handleDeleteGroup = () => {
        if (deletingGroup) return
        setDeletingGroup(true)
        service
            .delete(endpoints.splitwise.groupDetail.replace('{group_id}', groupId))
            .then(() => onBack())
            .catch(() => setDeletingGroup(false))
    }

    if (loading) {
        return <p className='bsFareNote'>Loading group...</p>
    }

    if (!group) {
        return (
            <div>
                <button type='button' className='groupDetailBack' onClick={onBack}><BackArrow /></button>
                <p className='bsFareNote' style={{ marginTop: 12 }}>Group not found.</p>
            </div>
        )
    }

    const linkedBookingIds = group.booking_ids || []
    const linkedBookings = shareBookings.filter((b) => linkedBookingIds.includes(b.id))
    const availableShareBookings = shareBookings.filter((b) => !linkedBookingIds.includes(b.id) && !shareBookingIds.includes(b.id))

    // The detail response now includes rich `bookings[]` (title/subtitle) —
    // undocumented, confirmed from a real response — fall back to bare
    // "Booking #id" rows from booking_ids for older responses without it.
    const sharedBookings = Array.isArray(group.bookings) && group.bookings.length
        ? group.bookings
        : linkedBookingIds.map((id) => ({ id, title: `Booking #${id}`, subtitle: '' }))

    return (
        <div>
            <div className='groupDetailHead'>
                <div className='groupDetailHead__left'>
                    <button type='button' className='groupDetailBack' onClick={onBack}>
                        <BackArrow />
                    </button>
                    <span className='groupDetailHead__icon' style={{ background: group.icon ? '#fff' : themeBackground }}>
                        {group.icon ? (
                            <img src={group.icon} alt="" className='groupCard__iconImage' />
                        ) : (
                            <img src={Location} alt="" />
                        )}
                    </span>
                    <div>
                        <div className='groupDetailHead__title'>
                            {activeTab === 'Expenses' ? `${group.name} • Expenses`
                                : activeTab === 'Chat' ? `${group.name} • Chat`
                                    : group.name}
                        </div>
                        <div className='groupDetailHead__subtitle'>
                            {activeTab === 'Expenses' ? 'Detailed list of all shared bills and split calculations'
                                : activeTab === 'Chat' ? `${group.member_count ?? (group.members?.length || 0)} Members`
                                    : `${group.member_count ?? (group.members?.length || 0)} Members • Created ${formatDate(group.created_at)}`}
                        </div>
                    </div>
                </div>
                <div className='groupDetailHead__actions'>
                    {isOwner && (
                        <>
                            {group.invitation_link && (
                                <button type='button' className='groupDetailBack' onClick={handleCopyInvite} title='Copy invite link'>
                                    <img src={Copy} alt="" style={{ width: 16, height: 16, filter: 'brightness(0)' }} />
                                </button>
                            )}
                            <button type='button' className='groupDetailBack' onClick={openEditGroup} title='Edit group'>
                                <EditIcon />
                            </button>
                            <button type='button' className='groupDetailBack' onClick={() => setDeleteConfirmOpen(true)} title='Delete group'>
                                <TrashIcon />
                            </button>
                        </>
                    )}
                    <button type='button' className='groupDetailAddExpenseBtn' onClick={onAddExpense}>+ Add Expense</button>
                    <button type='button' className='groupDetailSettleBtn' onClick={() => setSettleUpOpen(true)}>Settle Up</button>
                </div>
            </div>

            <div className='groupDetailTabs'>
                {TABS.map((tab) => (
                    <div
                        key={tab}
                        className={`groupDetailTab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </div>
                ))}
            </div>

            {activeTab === 'Chat' && (
                <GroupChat groupId={groupId} />
            )}

            {activeTab === 'Expenses' && (
                <div className='expensesTab'>
                    <div className='expensesStats'>
                        <div className='expensesStats__item'>
                            <div className='expensesStats__label'>Total Group Expenses</div>
                            <div className='expensesStats__value'>{formatMoney(totalSpend)}</div>
                        </div>
                        <div className='expensesStats__item'>
                            <div className='expensesStats__label'>Your Total Share</div>
                            <div className='expensesStats__value'>{formatMoney(yourShare)}</div>
                        </div>
                        <div className='expensesStats__item expensesStats__item--settled'>
                            <div className='expensesStats__label'>You Settled</div>
                            <div className='expensesStats__value'>{formatMoney(youSettled)}</div>
                        </div>
                        <div className='expensesStats__item expensesStats__item--remaining'>
                            <div className='expensesStats__label'>Remaining Balance</div>
                            <div className='expensesStats__value'>{formatMoney(youOwe)}</div>
                        </div>
                    </div>

                    <div className='expenseFilterTabs'>
                        {EXPENSE_FILTERS.map((filter) => (
                            <div
                                key={filter}
                                className={`expenseFilterTab ${expenseFilter === filter ? 'active' : ''}`}
                                onClick={() => setExpenseFilter(filter)}
                            >
                                {filter}
                            </div>
                        ))}
                    </div>

                    <div className='expenseList'>
                        {expensesLoading ? (
                            <div className='bookingRow__subtitle'>Loading expenses...</div>
                        ) : visibleExpenses.length ? visibleExpenses.map((expense) => (
                            <div className='expenseRow' key={expense.id} onClick={() => onOpenExpense?.(expense.id)} style={{ cursor: 'pointer' }}>
                                <span className='expenseRow__icon' style={{ background: '#F1FAF7' }}>💰</span>
                                <div className='expenseRow__info'>
                                    <div className='expenseRow__title'>{expense.title}</div>
                                    <div className='expenseRow__subtitle'>Paid by <span>{expense.paidByName}</span> • {capitalize(expense.splitType)} split</div>
                                </div>
                                <div className={`expenseRow__status expenseRow__status--${expense.status}`}>
                                    {expense.status === 'settled' ? '✓ Settled' : '• Pending'}
                                </div>
                                <div className='expenseRow__amountCol'>
                                    <div className='expenseRow__amount'>{formatMoney(expense.amount)}</div>
                                    <div className='expenseRow__yourShare'>
                                        {expense.yourShare != null ? `Your share: ${formatMoney(expense.yourShare)}` : ''}
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className='bookingRow__subtitle'>No expenses yet.</div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'Overview' && (
                <div className='groupDetailGrid'>
                    <div>
                        <div className='groupDetailCard'>
                            <div className='groupDetailCard__title' style={{ marginBottom: 16 }}>Balance Summary</div>
                            <div className='balanceSummary'>
                                <div className='balanceSummary__item'>
                                    <div className='balanceSummary__label'>Total Spend</div>
                                    <div className='balanceSummary__value balanceSummary__value--spend'>{formatMoney(totalSpend)}</div>
                                </div>
                                <div className='balanceSummary__item'>
                                    <div className='balanceSummary__label'>Your Share</div>
                                    <div className='balanceSummary__value'>{formatMoney(yourShare)}</div>
                                </div>
                                <div className='balanceSummary__item balanceSummary__item--owe'>
                                    <div className='balanceSummary__label'>You Owe</div>
                                    <div className='balanceSummary__value'>{formatMoney(youOwe)}</div>
                                </div>
                            </div>
                        </div>

                        <div className='groupDetailCard'>
                            <div className='groupDetailCard__head'>
                                <div className='groupDetailCard__title'>Shared Bookings</div>
                                <span className='groupDetailCard__link' onClick={openShareBooking}>+ Share Booking</span>
                            </div>
                            {sharedBookings.length ? sharedBookings.map((booking) => (
                                <div className='bookingRow' key={booking.id}>
                                    <span className='bookingRow__icon'>🎫</span>
                                    <div className='bookingRow__info'>
                                        <div className='bookingRow__title'>{booking.title}</div>
                                        {booking.subtitle ? <div className='bookingRow__subtitle'>{booking.subtitle}</div> : null}
                                    </div>
                                </div>
                            )) : (
                                <div className='bookingRow__subtitle'>No shared bookings yet.</div>
                            )}
                        </div>
                    </div>

                    <div>
                        {group.destination && (
                            <div className='groupDetailCard'>
                                <div className='tripDestination__label'>Trip Destination</div>
                                <div className='tripDestination__row'>
                                    <LocationPin />
                                    <span className='tripDestination__place'>{group.destination}</span>
                                </div>
                                {(group.dates || group.tripStatus) && (
                                    <div className='tripDestination__dates'>{group.dates} • <span>{group.tripStatus}</span></div>
                                )}
                            </div>
                        )}

                        <div className='groupDetailCard'>
                            <div className='groupDetailCard__head'>
                                <div className='groupDetailCard__title'>Group Members</div>
                                <div className='groupDetailCard__headActions'>
                                    {(group.members || []).some((m) => m.balance_type === 'owes') && (
                                        <button
                                            type='button'
                                            className='groupDetailCard__reminderAllBtn'
                                            disabled={Boolean(sendingReminderId)}
                                            onClick={() => handleSendReminder(null)}
                                        >
                                            <BellIcon /> {sendingReminderId === 'all' ? 'Sending...' : 'Remind All'}
                                        </button>
                                    )}
                                    {isOwner && <span className='groupDetailCard__link' onClick={openAddMember}>+ Add Member</span>}
                                </div>
                            </div>
                            {(group.members || []).map((member) => {
                                const direction = member.balance_type === 'owes' ? 'owe' : member.balance_type === 'owed' ? 'owed' : 'settled'
                                return (
                                    <div
                                        className='groupMemberRow'
                                        key={member.id}
                                        onClick={isOwner ? () => openManageMember(member) : undefined}
                                        style={isOwner ? { cursor: 'pointer' } : undefined}
                                    >
                                        <img src={member.image || SampleUser} alt="" className='groupMemberRow__avatar' />
                                        <div className='groupMemberRow__info'>
                                            <div className='groupMemberRow__name'>
                                                {member.name} <span>({capitalize(member.role)})</span>
                                                {member.status === 'pending' && <span className='groupMemberRow__pending'>Pending</span>}
                                            </div>
                                        </div>
                                        {direction === 'owe' && (
                                            <button
                                                type='button'
                                                className='groupMemberRow__reminderBtn'
                                                disabled={Boolean(sendingReminderId)}
                                                onClick={(e) => { e.stopPropagation(); handleSendReminder(member.id) }}
                                                title='Send payment reminder'
                                            >
                                                <BellIcon />
                                            </button>
                                        )}
                                        <div className={`groupMemberRow__amount groupMemberRow__amount--${direction}`}>
                                            {direction === 'owe' ? `Owes ${formatMoney(member.balance_amount)}`
                                                : direction === 'owed' ? `Owed ${formatMoney(member.balance_amount)}`
                                                    : 'Settled'}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            <Modal open={addMemberOpen} onClose={() => setAddMemberOpen(false)} maxWidth={480}>
                <div className='groupDetailCard__title' style={{ marginBottom: 4 }}>Add Members</div>
                <p className='bsFareNote' style={{ marginBottom: 16 }}>They'll be added as pending until they join via the invite link.</p>
                {newMemberRows.map((row, index) => (
                    <div className='addExpensePage__row' key={index}>
                        <div className='addExpensePage__field'>
                            <label>Name</label>
                            <input type='text' value={row.name} onChange={(e) => updateNewMemberRow(index, 'name', e.target.value)} />
                        </div>
                        <div className='addExpensePage__field'>
                            <label>Email</label>
                            <input type='text' value={row.email} onChange={(e) => updateNewMemberRow(index, 'email', e.target.value)} />
                        </div>
                        {newMemberRows.length > 1 && (
                            <button type='button' className='addExpensePage__cancelBtn' style={{ marginTop: 26 }} onClick={() => removeNewMemberRow(index)}>×</button>
                        )}
                    </div>
                ))}
                <span className='groupDetailCard__link' onClick={addNewMemberRow}>+ Add another person</span>
                <div className='addExpensePage__actions' style={{ marginTop: 20 }}>
                    <button type='button' className='addExpensePage__cancelBtn' onClick={() => setAddMemberOpen(false)}>Cancel</button>
                    <button type='button' className='addExpensePage__addBtn' disabled={addingMembers} onClick={handleAddMembers}>
                        {addingMembers ? 'Adding...' : 'Add Members'}
                    </button>
                </div>
            </Modal>

            <Modal open={Boolean(manageMember)} onClose={closeManageMember} maxWidth={420}>
                {manageMember && (
                    <>
                        <div className='groupDetailCard__title' style={{ marginBottom: 16 }}>{manageMember.name}</div>
                        {manageMember.status === 'pending' && (
                            <div className='addExpensePage__field' style={{ marginBottom: 18 }}>
                                <label>Email</label>
                                <input type='text' value={manageEmail} onChange={(e) => setManageEmail(e.target.value)} />
                                <button
                                    type='button'
                                    className='addExpensePage__addBtn'
                                    style={{ marginTop: 10 }}
                                    disabled={memberActionLoading}
                                    onClick={handleSaveEmail}
                                >
                                    {memberActionLoading ? 'Saving...' : 'Save Email'}
                                </button>
                            </div>
                        )}
                        {manageMember.role === 'admin' ? (
                            <p className='bsFareNote'>The group admin can't be removed.</p>
                        ) : (
                            <button
                                type='button'
                                className='addExpensePage__cancelBtn'
                                style={{ color: '#E7000B', width: '100%' }}
                                disabled={memberActionLoading}
                                onClick={handleRemoveMember}
                            >
                                {memberActionLoading ? 'Removing...' : 'Remove from Group'}
                            </button>
                        )}
                    </>
                )}
            </Modal>

            <Modal open={editGroupOpen} onClose={() => setEditGroupOpen(false)} maxWidth={420}>
                <div className='groupDetailCard__title' style={{ marginBottom: 16 }}>Edit Group</div>

                <div className='createGroupPhoto' style={{ marginBottom: 20 }}>
                    <span className='createGroupPhoto__circle' onClick={() => editIconInputRef.current?.click()}>
                        {editIconPreview ? <img src={editIconPreview} alt="" /> : <CameraIcon />}
                    </span>
                    <input
                        ref={editIconInputRef}
                        type='file'
                        accept='image/*'
                        style={{ display: 'none' }}
                        onChange={handleEditIconChange}
                    />
                    <span className='createGroupPhoto__link' onClick={() => editIconInputRef.current?.click()}>Change Photo</span>
                </div>

                <div className='addExpensePage__field' style={{ marginBottom: 16 }}>
                    <label>Group Name</label>
                    <input type='text' value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>
                <div className='addExpensePage__row'>
                    <div className='addExpensePage__field'>
                        <label>Tag</label>
                        <input type='text' placeholder='e.g. Goa' value={editTag} onChange={(e) => setEditTag(e.target.value)} />
                    </div>
                    <div className='addExpensePage__field'>
                        <label>Status</label>
                        <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                            <option value='open'>Open</option>
                            <option value='closed'>Closed</option>
                        </select>
                    </div>
                </div>
                <div className='addExpensePage__actions' style={{ marginTop: 20 }}>
                    <button type='button' className='addExpensePage__cancelBtn' onClick={() => setEditGroupOpen(false)}>Cancel</button>
                    <button type='button' className='addExpensePage__addBtn' disabled={savingGroup} onClick={handleSaveGroup}>
                        {savingGroup ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </Modal>

            <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth={380}>
                <div className='groupDetailCard__title' style={{ marginBottom: 8 }}>Delete "{group.name}"?</div>
                <p className='bsFareNote' style={{ marginBottom: 20 }}>This can't be undone. All expenses and members in this group will be removed.</p>
                <div className='addExpensePage__actions'>
                    <button type='button' className='addExpensePage__cancelBtn' onClick={() => setDeleteConfirmOpen(false)} disabled={deletingGroup}>Cancel</button>
                    <button
                        type='button'
                        className='addExpensePage__addBtn'
                        style={{ background: '#E7000B' }}
                        disabled={deletingGroup}
                        onClick={handleDeleteGroup}
                    >
                        {deletingGroup ? 'Deleting...' : 'Delete Group'}
                    </button>
                </div>
            </Modal>

            <Modal open={shareBookingOpen} onClose={() => setShareBookingOpen(false)} maxWidth={480}>
                <div className='groupDetailCard__title' style={{ marginBottom: 4 }}>Share Booking</div>
                <p className='bsFareNote' style={{ marginBottom: 18 }}>Attach one of your confirmed bookings to this group.</p>

                {linkedBookings.length > 0 && (
                    <div className='createGroupField'>
                        <label>Already Shared</label>
                        <div className='createGroupChips'>
                            {linkedBookings.map((booking) => (
                                <span className='createGroupChip' key={booking.id}>
                                    {bookingLabel(booking)}
                                    <button
                                        type='button'
                                        className='createGroupChip__remove'
                                        disabled={unlinkingBookingId === booking.id}
                                        onClick={() => removeLinkedBooking(booking.id)}
                                    >
                                        {unlinkingBookingId === booking.id ? '…' : '×'}
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <div className='createGroupField' style={{ marginBottom: shareBookingIds.length ? 12 : 4 }}>
                    <label>Add a Booking</label>
                    {shareBookingsLoading ? (
                        <p className='bsFareNote'>Loading your bookings...</p>
                    ) : (
                        <select
                            value=''
                            onChange={(e) => addShareBooking(e.target.value)}
                            disabled={!availableShareBookings.length}
                        >
                            <option value=''>{availableShareBookings.length ? 'Select a booking to add' : 'No more bookings to add'}</option>
                            {availableShareBookings.map((booking) => (
                                <option key={booking.id} value={booking.id}>{bookingLabel(booking)}</option>
                            ))}
                        </select>
                    )}
                </div>

                {shareBookingIds.length > 0 && (
                    <div className='createGroupField' style={{ marginBottom: 4 }}>
                        <label>Adding Now</label>
                        <div className='createGroupChips'>
                            {shareBookingIds.map((id) => {
                                const booking = shareBookings.find((b) => b.id === id)
                                return (
                                    <span className='createGroupChip' key={id}>
                                        {booking ? bookingLabel(booking) : `Booking #${id}`}
                                        <button type='button' className='createGroupChip__remove' onClick={() => removeShareBooking(id)}>×</button>
                                    </span>
                                )
                            })}
                        </div>
                    </div>
                )}

                <div className='addExpensePage__actions' style={{ marginTop: 20 }}>
                    <button type='button' className='addExpensePage__cancelBtn' onClick={() => setShareBookingOpen(false)}>Cancel</button>
                    <button type='button' className='addExpensePage__addBtn' disabled={!shareBookingIds.length || sharingBooking} onClick={handleShareBookings}>
                        {sharingBooking ? 'Sharing...' : 'Share Booking'}
                    </button>
                </div>
            </Modal>

            <Modal open={settleUpOpen} onClose={() => setSettleUpOpen(false)} maxWidth={380}>
                <div className='groupDetailCard__title' style={{ marginBottom: 8 }}>Settle Up</div>
                <p className='bsFareNote' style={{ marginBottom: 20 }}>
                    This group is currently <strong>{group.settlement_status === 'settled' ? 'Settled' : 'Unsettled'}</strong>.
                </p>
                <div className='addExpensePage__actions'>
                    <button type='button' className='addExpensePage__cancelBtn' onClick={() => setSettleUpOpen(false)}>Cancel</button>
                    <button type='button' className='addExpensePage__addBtn' disabled={togglingSettlement} onClick={handleToggleSettlement}>
                        {togglingSettlement ? 'Updating...' : group.settlement_status === 'settled' ? 'Mark as Unsettled' : 'Mark as Settled'}
                    </button>
                </div>
            </Modal>
        </div>
    )
}

export default GroupDetail
